import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, Renderer2} from "@angular/core";
import {ScreenLayoutEditorModel} from "../screen-layout-editor.model";
import {ITEM_TYPE, TouchItemData} from "../../../screen-layout.data";
import {ItemDisplayInfo} from "../item-display-info";
import {EditService} from "../../../../../framework/entity/edit/edit-page/edit.service";

/**
 * Menu within a screen layout that manages the touch items
 */
@Component({
    selector: "wbo-screen-layout-menu",
    templateUrl: "./screen-layout-menu.component.html",
    styleUrls: ["./screen-layout-menu.component.scss"]
})
export class ScreenLayoutMenuComponent implements AfterViewInit, OnDestroy {

    private static readonly HOVER_PAGING_TIME = 500;

    ITEM_TYPE = ITEM_TYPE;

    draggingItem: TouchItemData;
    dragoverNextTimestamp: number;
    dragoverPrevTimestamp: number;
    toggledItem: TouchItemData;

    private tearDownDragOverListenerFn: Function;

    constructor(public model: ScreenLayoutEditorModel,
                protected element: ElementRef,
                protected ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef,
                public editService: EditService,
                private renderer: Renderer2) {
    }

    ngAfterViewInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.tearDownDragOverListenerFn = this.renderer.listen(this.element.nativeElement,
                "dragover",
                (event: DragEvent) => {
                    const targetItem: ItemDisplayInfo = event["targetItem"];
                    if (!this.draggingItem || !targetItem || targetItem === this.draggingItem) {
                        event.dataTransfer.dropEffect = "none";
                    } else {
                        event.dataTransfer.dropEffect = "none";
                        if (targetItem.itemType === ITEM_TYPE.MENU_PREV) {
                            this.dragoverPrev(event);
                        } else if (targetItem.itemType === ITEM_TYPE.MENU_NEXT) {
                            this.dragoverNext(event);
                        } else {
                            event.dataTransfer.dropEffect = "move";
                        }
                    }
                    event.preventDefault();
                    event.stopPropagation();
                });
        });
    }

    dragstart(event: DragEvent, item: TouchItemData): void {
        event.dataTransfer.effectAllowed = "move";
        if (this.model.painting || this.editService.entityRestricted) {
            event.preventDefault();
        } else {
            this.draggingItem = item;
            this.model.dragging = true;
        }
    }

    dragend(): void {
        this.draggingItem = undefined;
        this.model.dragging = false;
    }

    drop(event: DragEvent, item: TouchItemData): void {
        if (this.draggingItem && (!item || item !== this.draggingItem)) {
            this.model.moveItem(this.draggingItem, item);
        }
        this.draggingItem = undefined;
        this.model.dragging = false;
    }

    dragoverPrev(event: DragEvent): void {
        if (!this.dragoverPrevTimestamp) {
            this.dragoverPrevTimestamp = event.timeStamp;
        }
        if (event.timeStamp > this.dragoverPrevTimestamp + ScreenLayoutMenuComponent.HOVER_PAGING_TIME) {
            this.dragoverPrevTimestamp = undefined;
            this.model.previousMenuPage();
            // cancelling the dragging as when moving to another page, the source element that holds the dragend event is destroyed causing it to never detect the end of dragging. So we are
            // cancelling here as it is most used for the dragging image to not have the commands
            this.model.dragging = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    dragleavePrev(): void {
        this.dragoverPrevTimestamp = undefined;
    }

    dragoverNext(event: DragEvent): void {
        if (!this.dragoverNextTimestamp) {
            this.dragoverNextTimestamp = event.timeStamp;
        }
        if (event.timeStamp > this.dragoverNextTimestamp + ScreenLayoutMenuComponent.HOVER_PAGING_TIME) {
            this.dragoverNextTimestamp = undefined;
            this.model.nextMenuPage();
            // cancelling the dragging as when moving to another page, the source element that holds the dragend event is destroyed causing it to never detect the end of dragging. So we are
            // cancelling here as it is most used for the dragging image to not have the commands
            this.model.dragging = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    dragleaveNext(): void {
        this.dragoverNextTimestamp = undefined;
    }

    commandsToggled(toggled: boolean, item: TouchItemData): void {
        this.toggledItem = toggled ? item : undefined;
    }

    ngOnDestroy(): void {
        this.tearDownDragOverListenerFn();
    }
}
